import { Fragment } from 'react'
import cx from 'classnames'
import isEmpty from 'lodash/isEmpty'

import { ChevronRight } from 'common/icons'

import CheckboxComponent from '../checkbox/Checkbox'

import { TreeNode } from './Catalog'
import MenuItem from './MenuItem'

type Props = {
  items: TreeNode | string[]
  checkedItems?: TreeNode | string[]
  selectedPath: string[]
  onClickCheckbox: ((item: string, depth: number) => void) | undefined
  onSelect: (item: string, depth: number) => void
  depth: number
  isSearching: boolean
}

const RecursiveSubMenu = ({
  items,
  checkedItems,
  selectedPath,
  onSelect,
  onClickCheckbox,
  depth,
  isSearching,
}: Props) => {
  if ((depth !== 0 && selectedPath.length < depth) || items.length === 0) {
    return null
  }
  const isChecked = (item: string) => {
    if (!checkedItems) {
      return false
    }
    return Array.isArray(checkedItems)
      ? checkedItems.some((current) => current === item)
      : checkedItems[item] !== undefined
  }
  return (
    <>
      <div
        className={cx('overflow-y-auto overflow-x-hidden', {
          'max-h-150 w-100': !isSearching,
          'w-full': isSearching,
        })}
      >
        {Array.isArray(items) ? (
          <>
            {items.map((item, index) => (
              <MenuItem
                key={index}
                className="hover:bg-blueGray-200 hover:font-medium cursor-pointer truncate text-left"
              >
                <div className="flex items-center">
                  {onClickCheckbox && (
                    <CheckboxComponent
                      isChecked={isChecked(item)}
                      onChange={() => {
                        onClickCheckbox(item, depth)
                      }}
                    />
                  )}
                  <span className="truncate">{item}</span>
                </div>
              </MenuItem>
            ))}
          </>
        ) : (
          Reflect.ownKeys(items).map((item) => {
            if (typeof item === 'symbol') {
              return undefined
            }
            const parentItem = items[item]
            const parentSelectedItem = checkedItems?.[item]
            return (
              <Fragment key={item}>
                <MenuItem
                  className={cx(
                    'hover:bg-blueGray-200 cursor-pointer hover:font-medium w-full break-words',
                    {
                      'bg-blueGray-200 font-medium':
                        selectedPath[depth] === item,
                    }
                  )}
                  onSelect={() => {
                    onSelect(item, depth)
                  }}
                >
                  <div className="flex items-center">
                    {onClickCheckbox && (
                      <CheckboxComponent
                        isChecked={isChecked(item)}
                        indeterminate={
                          isChecked(item) &&
                          (Array.isArray(parentItem)
                            ? parentItem.length !== parentSelectedItem.length
                            : Object.values(parentItem).length !==
                              Object.values(parentSelectedItem).length)
                        }
                        onChange={() => {
                          onClickCheckbox(item, depth)
                        }}
                      />
                    )}
                    <div className="flex justify-between gap-4 flex-1 min-w-0">
                      <div className="truncate text-left">{item}</div>
                    </div>
                    <ChevronRight
                      className={cx({
                        hidden: isEmpty(items[item]),
                      })}
                      size={4}
                    />
                  </div>
                </MenuItem>
              </Fragment>
            )
          })
        )}
      </div>

      <RecursiveSubMenu
        items={items[selectedPath[depth]]}
        onSelect={onSelect}
        checkedItems={checkedItems?.[selectedPath[depth]]}
        onClickCheckbox={onClickCheckbox}
        selectedPath={selectedPath}
        depth={depth + 1}
        isSearching={isSearching}
      />
    </>
  )
}

export default RecursiveSubMenu
